import { computedAnswerOptions, computedIntegerListOptions } from "./computeAnswerOptions";
import { isEmpty } from "@utils/toolBox";
import { useSurveyCardsStore } from "@stores/survey/surveyCards";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";

export const computedValuesOptions = (card, sourceCard) => {
  const surveyCardsStore = useSurveyCardsStore();
  const { findCardByToken } = surveyCardsStore;

  // custom cards
  if (isEmpty(card)) return [];

  const specs = card.specs ?? card;
  const sourceSpecs = sourceCard?.specs ?? sourceCard;

  if (card.type === "o_custom") {
    return computedCustomCardValuesOptions(card, sourceCard);
  }

  if (card.type === "o_quota") {
    return [
      { label: "Quota full", value: "1" },
      { label: "Quota not full", value: "0" },
    ];
  }

  if (card.type === "q_language") {
    return computedLanguageValuesOptions(card);
  }

  if (card.type === "o_loop") {
    if (specs.loop_type === "answers") {
      return computedIntegerListOptions(1, sourceSpecs?.answers?.length); //List of integers that will correspond to the indices of the answers from the source question
    } else if (specs.loop_type === "user_attribute")
      if (sourceSpecs.method_name === "answers_sample") {
        return computedIntegerListOptions(
          1,
          findCardByToken({ token: sourceSpecs.question_reference }).specs.answers?.length,
        ); //because o_custom redirect to a q_multiple with several answers
      } else if (sourceSpecs.method_name === "random_numbers") {
        return computedIntegerListOptions(1, sourceSpecs.max);
      } else {
        return;
      }
  }

  if (card.type === "q_nps" || (card.type === "q_matrix" && specs.matrix_type === "nps")) {
    if (specs.skippable) return [...computedIntegerListOptions(0, 10), { label: "Skipped", value: "999" }];
    return computedIntegerListOptions(0, 10);
  }

  if (card.type === "q_rating" || (card.type === "q_matrix" && specs.matrix_type === "rating")) {
    if (specs.skippable)
      return [
        ...computedIntegerListOptions(parseInt(specs.rating_limits.min, 10), parseInt(specs.rating_limits.max, 10)),
        { label: "Skipped", value: "999" },
      ];
    return computedIntegerListOptions(parseInt(specs.rating_limits.min, 10), parseInt(specs.rating_limits.max, 10));
  }

  if (card.type === "q_slider") {
    return computedIntegerListOptions(parseInt(specs.slider_limits.min, 10), parseInt(specs.slider_limits.max, 10));
  }

  // open questions and some matrix questions
  const openQuestionTypes = [
    "q_open",
    "q_zipcode",
    "q_open_multiple",
    "q_numeric",
    "q_email",
    "q_audio",
    "q_image",
    "q_video",
  ];
  if (openQuestionTypes.includes(card?.type)) {
    if (specs.skippable) return [{ label: "Skipped", value: "999" }];
    return [];
  }

  // other cards
  let answersField = "answers";
  if (card.type === "o_ab") answersField = "populations";
  if (card.type === "o_cells") answersField = "cells";
  if (card.type === "o_user_attributes") answersField = "attribute_values";
  let values = computedAnswerOptions({
    answers: specs[answersField] || [],
    valueType: "token",
  });
  if (specs.skippable) values = [{ label: "Skipped", value: "999" }, ...values];
  return values;
};

export const computedCustomCardValuesOptions = (card, sourceCard) => {
  const specs = card.specs ?? card;
  const sourceSpecs = sourceCard?.specs ?? sourceCard;

  let options = [];
  const method = specs.method_name;
  if (method === "is_in_list") {
    options = [
      { label: "Is in list", value: "1" },
      { label: "Is not in list", value: "0" },
    ];
  } else if (method === "random_numbers") {
    options = options.concat(computedIntegerListOptions(Number(specs.min), Number(specs.max)));
  } else if (method === "set_n_answers") {
    // The count runs from 0 to every non-excluded answer picked.
    const max = (sourceSpecs?.answers?.length || 0) - (specs.exclude || []).length;
    options = options.concat(computedIntegerListOptions(0, max));
  } else if (method === "answers_sample") {
    const filteredAnswers = sourceSpecs?.answers?.filter((a) => !(specs.exclude || []).includes(a.token));
    options = computedAnswerOptions({
      answers: filteredAnswers,
      valueType: "code",
    });
  }
  return options;
};

export const computedLanguageValuesOptions = () => {
  const surveyLocalesStore = useSurveyLocalesStore();
  return surveyLocalesStore.localeOptions;
};
